import { ExtendedProductDetailProduct, VariantDefiningAttribute } from 'shared/types/product/Product';
import { isSelectableAxis } from './optionAxes';

interface AttributeOption {
  value: string | number;
  label?: string;
}

/** `attributeOptions` is attached by the PDP data mapper and is not on the shared product type. */
type ProductWithAttributeOptions = ExtendedProductDetailProduct & {
  attributeOptions?: Record<string, AttributeOption[]>;
};

/**
 * All values a shopper could pick for one axis.
 *
 * Product-wide lists win over the per-SKU map: `variantsMap` only holds the combinations reachable
 * from the current SKU, so using it first would shrink an axis to a single value and silently turn
 * a real choice into a spec row.
 */
export const getAxisOptionValues = (
  product: ExtendedProductDetailProduct,
  sku: string,
  attribute: VariantDefiningAttribute,
): string[] => {
  const productWithOptions = product as ProductWithAttributeOptions;

  const globalValues: Array<string | number> = attribute.primitiveValues?.length
    ? attribute.primitiveValues
    : (productWithOptions.attributeOptions?.[attribute.key]?.map((option) => option.value) ?? []);

  const values = globalValues.length ? globalValues : (product.variantsMap?.[sku]?.[attribute.key] ?? []);

  // Numeric attribute values (heights, lengths) arrive as numbers from some sources and strings from
  // others; the option UI and every comparison here are string-based, so normalize once.
  return values.map(String);
};

export const distinctAxisValueCount = (
  product: ExtendedProductDetailProduct,
  sku: string,
  attribute: VariantDefiningAttribute,
) => new Set(getAxisOptionValues(product, sku, attribute)).size;

/**
 * The axes a shopper actually chooses on: single-valued attributes are facts about the product, not
 * choices. The buy box and the mosaic's color chips both resolve SKUs against this one list, so a
 * chip can never select something the buy box would refuse.
 */
export const getSelectableAxes = (product: ExtendedProductDetailProduct, sku: string) =>
  (product.variantDefiningAttributes ?? []).filter((attribute) =>
    isSelectableAxis(attribute.key, distinctAxisValueCount(product, sku, attribute)),
  );
